import React, { useState, useEffect } from "react";
import {
  Card,
  Text,
  Group,
  Stack,
  ActionIcon,
  Box,
  Title,
  Button,
} from "@mantine/core";
import { IconEdit, IconPlus } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import EditRecruitingSourceModal from "./EditRecruitingSourceModal";

// Simple Item Component
const RecruitingSourceItem = ({ source, onEdit }) => {
  return (
    <Box mb="sm">
      <Box
        style={{
          padding: ".75rem",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          backgroundColor: "#fafafa",
        }}
      >
        <Group justify="space-between" align="center" w="100%">
          <Text
            size="lg"
            style={{ flex: 1, textAlign: "center", fontWeight: 500 }}
            lineClamp={1}
          >
            {source.source_name}
          </Text>

          <ActionIcon
            variant="subtle"
            size="md"
            onClick={() => onEdit(source)}
            aria-label={`Edit ${source.source_name}`}
          >
            <IconEdit size={20} />
          </ActionIcon>
        </Group>
      </Box>
    </Box>
  );
};

const RecruitingSourcesCard = () => {
  const { getVerifiedJWT } = useAuth();
  const [sources, setSources] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);

  // Fetch recruiting sources from API
  const fetchRecruitingSources = async () => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        return;
      }

      const response = await fetch(
        getApiEndpoint("/v2/company/recruiting-sources"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const data = result.data || [];
        setSources(data);
      }
    } catch (err) {}
  };

  // Handle updating a recruiting source
  const handleUpdateSource = async updatedSource => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v1/company/recruiting-source/${updatedSource.id}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            source_name: updatedSource.name,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        // Update the local state with the response from API
        const updatedSources = sources.map(source =>
          source.id === updatedSource.id ? result.data || result : source
        );
        setSources(updatedSources);
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to update recruiting source"
        );
      }
    } catch (err) {
      throw new Error(err.message || "Failed to update recruiting source");
    }
  };

  // Handle adding a new recruiting source
  const handleAddSource = async newSource => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint("/v1/company/recruiting-source"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            source_name: newSource.name,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        // Add the new recruiting source returned from API to the local state
        setSources(prev => [...prev, result.data || result]);
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to create recruiting source"
        );
      }
    } catch (err) {
      throw err; // Re-throw to be handled by the modal
    }
  };

  // Handle deleting a recruiting source
  const handleDeleteSource = async sourceId => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v1/company/recruiting-source/${sourceId}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        // Remove the deleted recruiting source from local state
        setSources(prev => prev.filter(source => source.id !== sourceId));
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to delete recruiting source"
        );
      }
    } catch (err) {
      throw new Error(err.message || "Failed to delete recruiting source");
    }
  };

  const handleEditClick = source => {
    setSelectedSource(source);
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setSelectedSource(null);
  };

  const handleSave = async updatedSource => {
    try {
      await handleUpdateSource(updatedSource);
      handleModalClose();
    } catch (error) {
      throw error;
    }
  };

  const handleAddModalClose = () => {
    setAddModalOpen(false);
  };

  const handleAddSave = async newSource => {
    try {
      await handleAddSource(newSource);
      handleAddModalClose();
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    fetchRecruitingSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3}>Recruiting Sources</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setAddModalOpen(true)}
            variant="filled"
            size="sm"
          >
            Add Source
          </Button>
        </Group>

        {!sources || sources.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No recruiting sources configured yet
          </Text>
        ) : (
          <Stack gap="sm">
            {sources.map(source => (
              <RecruitingSourceItem
                key={source.id}
                source={source}
                onEdit={handleEditClick}
              />
            ))}
          </Stack>
        )}
      </Card>

      <EditRecruitingSourceModal
        opened={editModalOpen}
        onClose={handleModalClose}
        source={selectedSource}
        onSave={handleSave}
        onDelete={handleDeleteSource}
      />

      <EditRecruitingSourceModal
        opened={addModalOpen}
        onClose={handleAddModalClose}
        source={null}
        onSave={handleAddSave}
      />
    </>
  );
};

export default RecruitingSourcesCard;
